import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Search, X, ArrowUpRight, Filter } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`, {
            params: activeCategory ? { category: activeCategory } : {}
          }),
          axios.get(`${API}/categories`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeCategory]);

  const handleCategoryChange = (slug) => {
    if (slug === '') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', slug);
    }
    setSearchParams(searchParams);
    setShowMobileFilter(false);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = filteredProducts.length;

  return (
    <div className="min-h-screen bg-[#f9f9f7]" data-testid="products-page">
      {/* Header - Premium */}
      <section className="relative overflow-hidden pt-20 sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1d35] to-[#0a1628]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-1/2 h-full" style={{
            backgroundImage: `radial-gradient(circle at 70% 30%, rgba(197, 160, 89, 0.3) 0%, transparent 50%)`
          }} />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-12 sm:py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-3 text-secondary-500 mb-4">
              <span className="w-8 sm:w-12 h-px bg-secondary-500" />
              <span className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] sm:tracking-[0.3em]">
                {t('products.view_all')}
              </span>
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-4 sm:mb-6">
              {t('products.title')}
            </h1>
            <p className="text-base sm:text-lg text-white/60 max-w-xl">
              {t('products.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-8 sm:py-12 md:py-16">
        {/* Search & Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4 mb-8 sm:mb-12"
        >
          {/* Search + Mobile Filter Toggle */}
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('products.search_placeholder') || "Search products..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 sm:h-14 rounded-none border-border bg-white text-sm"
                data-testid="product-search"
              />
            </div>
            
            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilter(true)}
              className="lg:hidden flex items-center gap-2 px-4 h-12 sm:h-14 bg-primary-900 text-white text-xs font-bold uppercase tracking-wider"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>
          </div>

          {/* Desktop Category Pills */}
          <div className="hidden lg:flex flex-wrap gap-2">
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-4 xl:px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                activeCategory === ''
                  ? 'bg-primary-900 text-white'
                  : 'bg-white border border-border text-muted-foreground hover:border-primary-900 hover:text-primary-900'
              }`}
              data-testid="filter-all"
            >
              {t('products.filter_all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 xl:px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-900 text-white'
                    : 'bg-white border border-border text-muted-foreground hover:border-primary-900 hover:text-primary-900'
                }`}
                data-testid={`filter-${cat.slug}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Active Filter & Count */}
        <div className="flex items-center justify-between mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-border">
          <p className="text-sm text-muted-foreground">
            {t('products.showing')} <span className="font-mono font-medium text-primary-900">{activeCount}</span> {t('products.products_label')}
            {activeCategory && (
              <span className="ml-2">
                {t('products.in_category')} <span className="font-medium text-primary-900">
                  {categories.find(c => c.slug === activeCategory)?.name}
                </span>
                <button
                  onClick={() => handleCategoryChange('')}
                  className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary-900"
                >
                  <X className="w-3 h-3" />
                  {t('products.clear')}
                </button>
              </span>
            )}
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white h-80 sm:h-96 animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 sm:py-24 bg-white border border-border">
            <p className="text-lg sm:text-xl font-heading font-bold text-primary-900 mb-4">{t('products.no_products')}</p>
            <p className="text-muted-foreground mb-6 sm:mb-8">{t('products.try_adjusting')}</p>
            <Button
              onClick={() => {
                setSearchQuery('');
                handleCategoryChange('');
              }}
              className="bg-primary-900 hover:bg-primary-800 text-white rounded-none px-6 sm:px-8 py-3 sm:py-4 text-xs font-bold uppercase tracking-widest"
            >
              {t('products.clear_all')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ProductCard product={product} index={index} />
              </motion.div>
            ))}
          </div>
        )}

        {/* RFQ CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 md:mt-20 bg-primary-900 p-6 sm:p-10 md:p-16"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 sm:gap-8">
            <div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-white mb-2">
                Can't find what you're looking for?
              </h3>
              <p className="text-white/60 text-sm sm:text-base">
                Submit a custom RFQ and we'll source it for you.
              </p>
            </div>
            <Link to="/rfq" className="flex-shrink-0">
              <Button className="w-full md:w-auto bg-secondary-500 hover:bg-secondary-400 text-primary-900 rounded-none px-8 sm:px-10 py-5 sm:py-6 text-xs font-bold uppercase tracking-widest group whitespace-nowrap">
                Request Quote
                <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilter && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 lg:hidden"
        >
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileFilter(false)}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="absolute bottom-0 left-0 right-0 bg-white max-h-[70vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-heading font-bold text-primary-900">Filter by Category</h3>
              <button
                onClick={() => setShowMobileFilter(false)}
                className="p-2 hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => handleCategoryChange('')}
                className={`w-full text-left px-4 py-4 text-sm font-medium transition-all ${
                  activeCategory === ''
                    ? 'bg-primary-900 text-white'
                    : 'bg-muted text-primary-900 hover:bg-muted/70'
                }`}
              >
                All Categories
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`w-full text-left px-4 py-4 text-sm font-medium transition-all ${
                    activeCategory === cat.slug
                      ? 'bg-primary-900 text-white'
                      : 'bg-muted text-primary-900 hover:bg-muted/70'
                  }`}
                >
                  {cat.name}
                  <span className="float-right text-xs opacity-60">{cat.product_count}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Products;
